import { useIsAdmin } from "@hooks/useAuth.ts";
import { useGroupStore } from "@store/groupStore.ts";
import { useCallback, useRef, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import ConfTab4, {
  type ConfTab4Ref,
} from "@/components/feature/roster_configure/ConfTab4";
import useRosterConfig from "@/hooks/useRosterConfig";
import { useScheduleStore } from "@/store/scheduleStore";
import { cn } from "@/lib/utils";

/**
 * 근무표 생성 화면의 원티드 설정 전용 Dialog.
 * 설정 본문은 설정 페이지의 원티드 설정(ConfTab4)을 그대로 재사용한다.
 */
function ModalWantedConfig({ onClose }: { onClose: () => void }) {
  const isAdmin = useIsAdmin();
  const { selectedGroupId } = useGroupStore();
  const { data: serverConfig } = useRosterConfig();
  const { curDate } = useScheduleStore();
  const configRef = useRef<ConfTab4Ref>(null);
  const [configState, setConfigState] = useState({
    hasChanges: false,
    isPending: false,
  });
  const fixedMonth =
    curDate?.year != null && curDate?.month != null
      ? { year: curDate.year, month: curDate.month }
      : undefined;

  const handleSave = useCallback(() => {
    configRef.current?.handleSave();
  }, []);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        overlayClassName="bg-black/45"
        className={cn(
          "flex max-h-[calc(100vh-64px)] w-[680px] max-w-[calc(100vw-32px)] flex-col gap-0 overflow-hidden rounded-[14px] border-0 bg-white p-0 shadow-[0_24px_80px_rgba(15,23,42,0.22)]"
        )}
      >
        <DialogHeader className="border-b border-zinc-200 bg-white px-6 py-4 text-left">
          <DialogTitle className="font-pretendard! text-[18px] font-semibold text-zinc-900">
            원티드 설정
          </DialogTitle>
          <DialogDescription className="font-pretendard! text-[13px] text-zinc-500">
            근무표 생성 월 기준으로 원티드 신청 제한을 설정합니다.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-auto bg-zinc-50/60 px-4 pb-6 pt-4 scrollbar-float">
          <ConfTab4
            ref={configRef}
            visible
            isAdmin={isAdmin}
            selectedGroupId={selectedGroupId ?? null}
            serverConfig={serverConfig}
            fixedMonth={fixedMonth}
            hideDefaultActions
            onStateChange={setConfigState}
          />
        </div>
        <DialogFooter className="border-t border-zinc-200 bg-white px-6 py-4 sm:justify-center">
          <Button
            type="button"
            onClick={handleSave}
            disabled={!configState.hasChanges || configState.isPending}
            className="h-12 min-w-[180px] rounded-full bg-sig px-8 font-pretendard! text-[15px] font-semibold text-white hover:bg-sig/90 disabled:bg-zinc-200 disabled:text-zinc-400"
          >
            {configState.isPending ? "저장 중..." : "저장하기"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ModalWantedConfig;
